import { useFeatureGateContext } from './FeatureGateProvider'
import type {
  FeatureBadge,
  FeVisibility,
} from '@/services/api/billing/useGetBillingSubscription'

export interface ResolvedFeature {
  lookupKey: string
  name: string
  available: boolean
  value: string | null
  feVisibility: FeVisibility
  upsellModal: boolean
  /** basic | pro */
  plan: string
  /** How the locked feature is presented (API-driven); null while available. */
  badge: FeatureBadge | null
}

/**
 * Resolve a plan feature by lookup_key.
 *
 * While the plan catalogue is still loading, features are reported as available
 * so the UI does not flash locked states on every navigation.
 */
export function useFeature(lookupKey: string): ResolvedFeature {
  const { features, loaded } = useFeatureGateContext()
  const feature = features[lookupKey]

  if (!feature) {
    return {
      lookupKey,
      name: lookupKey,
      available: !loaded, // optimistic until loaded, then unknown key => unavailable
      value: null,
      feVisibility: 'lock',
      upsellModal: true,
      plan: 'basic',
      badge: null,
    }
  }

  return {
    lookupKey,
    name: feature.name,
    available: !loaded || feature.available,
    value: feature.value,
    feVisibility: feature.fe_visibility,
    upsellModal: feature.upsell_modal,
    plan: feature.plan ?? 'basic',
    badge: feature.badge ?? null,
  }
}

/**
 * True once the catalogue says the feature is locked AND must not be shown at
 * all (fe_visibility "hide", e.g. the Xero / Zoho integrations on Basic).
 */
export function useFeatureHidden(lookupKey: string): boolean {
  const { loaded } = useFeatureGateContext()
  const { available, feVisibility } = useFeature(lookupKey)
  return loaded && !available && feVisibility === 'hide'
}

/**
 * Badge for an upsell on this feature: the API's per-feature badge, else (a
 * limit that is available but capped, e.g. history.months) whatever buying
 * Pro currently means — Coming soon until Pro is purchasable.
 */
export function useUpsellBadge(lookupKey: string): FeatureBadge {
  const { pro } = useFeatureGateContext()
  return (
    useFeature(lookupKey).badge ?? (pro.purchasable ? 'upgrade' : 'coming_soon')
  )
}

export function useHasFeature(lookupKey: string): boolean {
  return useFeature(lookupKey).available
}

export function useFeatureValue<T extends string | number>(
  lookupKey: string,
  fallback: T,
): T {
  const { value, available } = useFeature(lookupKey)
  if (!available || value === null) return fallback
  return (typeof fallback === 'number' ? Number(value) : value) as T
}

export function useFeaturesLoaded(): boolean {
  return useFeatureGateContext().loaded
}
